class TextHoverAnimation {
    constructor() {
        // Target specific main buttons, excluding close buttons, slider buttons, etc.
        this.selector = '.button:not(.button-close):not(.slider-button):not(.nav-fx-link), .btn:not(.button-close), .shopify-payment-button__button';
        this.injectStyles();
        this.init();
        this.observe();
    }

    injectStyles() {
        const style = document.createElement('style');
        style.id = 'bonj-hover-override';
        style.textContent = `
            /* Completely nuke the rolling and cascade text animations on targeted elements */
            .bonj-custom-hover .button__roll, 
            .bonj-custom-hover .button__char, 
            .bonj-custom-hover .button__label::before,
            .bonj-custom-hover .nav-fx__roll,
            .bonj-custom-hover .nav-fx__char {
                transform: none !important;
                transition: none !important;
                animation: none !important;
                opacity: 1 !important;
            }
            
            /* Hide all duplicate/second text items */
            .bonj-custom-hover .button__roll-item:nth-child(2),
            .bonj-custom-hover .nav-fx__roll-item:nth-child(2),
            .bonj-custom-hover .button__label::before,
            .bonj-custom-hover .nav-fx--cascade::before,
            .bonj-custom-hover [data-text]::before,
            .bonj-custom-hover [data-hover]::before {
                display: none !important;
                content: none !important;
            }
            
            /* Fix hover opacity & scale */
            .bonj-custom-hover:hover {
                opacity: 1 !important;
                transform: none !important;
                scale: none !important;
            }
        `;
        document.head.appendChild(style);
    }

    getLastTextNode(node) {
        if (!node) return null;
        // Skip hidden elements (like aria-hidden duplicates)
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') return null;
            // Skip the second roll item (it's always the aria-hidden duplicate)
            if (node.classList && node.classList.contains('nav-fx__roll-item')) {
                const parent = node.parentNode;
                if (parent && parent.children.length > 1 && node !== parent.children[0]) return null;
            }
        }
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '') {
            return node;
        }
        const skipTags = ['SVG', 'SCRIPT', 'STYLE', 'IMG', 'IFRAME'];
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
            const child = node.childNodes[i];
            if (child.nodeType === Node.ELEMENT_NODE && skipTags.includes(child.tagName)) continue;
            const textNode = this.getLastTextNode(child);
            if (textNode) return textNode;
        }
        return null;
    }

    processElement(el) {
        if (el.dataset.bonjHoverProcessed) return;
        el.dataset.bonjHoverProcessed = 'true';
        el.classList.add('bonj-custom-hover');

        // For cascade nav links, the text is split into individual nav-fx__char spans
        // We need to handle those differently — just re-use the data-text attribute
        const cascadeEl = el.querySelector('.nav-fx--cascade');
        if (cascadeEl) {
            this.processCascadeNav(el, cascadeEl);
            return;
        }

        const lastTextNode = this.getLastTextNode(el);
        if (!lastTextNode) return;

        const originalValue = lastTextNode.nodeValue;
        const match = originalValue.match(/([a-zA-Z0-9])(\s*)$/);
        if (!match) return;

        const lastChar = match[1];
        const trailingSpaces = match[2];
        const textWithoutLastChar = originalValue.slice(0, match.index);
        const hoveredText = textWithoutLastChar + lastChar + lastChar + lastChar + trailingSpaces;

        const updateDataAttrs = (newText) => {
            [el, ...el.querySelectorAll('[data-text], [data-hover]')].forEach(element => {
                if (element.hasAttribute('data-text')) element.setAttribute('data-text', newText.trim());
                if (element.hasAttribute('data-hover')) element.setAttribute('data-hover', newText.trim());
            });
        };

        el.addEventListener('mouseenter', () => {
            lastTextNode.nodeValue = hoveredText;
            updateDataAttrs(hoveredText);
        });

        el.addEventListener('mouseleave', () => {
            lastTextNode.nodeValue = originalValue;
            updateDataAttrs(originalValue);
        });
    }

    processCascadeNav(el, cascadeEl) {
        // Cascade nav has each letter as its own span — grab the full original text from data-text
        const originalText = cascadeEl.getAttribute('data-text') || cascadeEl.textContent.trim();
        if (!originalText) return;

        const match = originalText.match(/([a-zA-Z0-9])(\s*)$/);
        if (!match) return;

        const lastChar = match[1];
        const trailingSpaces = match[2];
        const textWithoutLastChar = originalText.slice(0, match.index);
        const hoveredText = textWithoutLastChar + lastChar + lastChar + lastChar + trailingSpaces;

        // We can't split into nav-fx__char spans easily, so we just replace 
        // the entire cascade span with a plain text node on hover
        el.addEventListener('mouseenter', () => {
            cascadeEl.dataset.originalHtml = cascadeEl.innerHTML;
            cascadeEl.setAttribute('data-text', hoveredText);
            cascadeEl.textContent = hoveredText;
        });

        el.addEventListener('mouseleave', () => {
            cascadeEl.setAttribute('data-text', originalText);
            if (cascadeEl.dataset.originalHtml) {
                cascadeEl.innerHTML = cascadeEl.dataset.originalHtml;
            }
        });
    }

    init() {
        document.querySelectorAll(this.selector).forEach(el => this.processElement(el));
    }

    observe() {
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.matches && node.matches(this.selector)) {
                            this.processElement(node);
                        }
                        node.querySelectorAll(this.selector).forEach(el => this.processElement(el));
                    }
                });
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new TextHoverAnimation());
} else {
    new TextHoverAnimation();
}
